import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reporter } from '@pf/shared';
import { useCreateContact, useBatchImportReporters, BatchImportResult } from '../hooks/useContacts';
import { useAddContactToList, usePressLists } from '../hooks/usePressLists';

// ── PressListSelector ─────────────────────────────────────────────────────────

function PressListSelector({
  value,
  onChange,
}: {
  value: string;
  onChange: (id: string) => void;
}) {
  const { data, isLoading } = usePressLists();
  const lists = (data?.pressLists ?? []).filter((l) => !l.archivedAt);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Add to press list{' '}
        <span className="text-gray-400 font-normal">(optional)</span>
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isLoading}
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      >
        <option value="">No press list</option>
        {lists.map((list) => (
          <option key={list.id} value={list.id}>
            {list.name}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Shared utilities ──────────────────────────────────────────────────────────

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

// ── ImportReporterModal ───────────────────────────────────────────────────────

export interface ImportReporterModalProps {
  reporter: Reporter;
  onClose: () => void;
}

export function ImportReporterModal({ reporter, onClose }: ImportReporterModalProps) {
  const navigate = useNavigate();
  const createContact = useCreateContact();
  const addToList = useAddContactToList();
  const [pressListId, setPressListId] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const primaryPubName = reporter.primaryPublication?.name;
  const nameParts = reporter.name.trim().split(/\s+/);
  const firstName = reporter.firstName ?? nameParts[0] ?? '';
  const lastName = reporter.lastName ?? nameParts.slice(1).join(' ') ?? '';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await createContact.mutateAsync({
        firstName,
        lastName: lastName || firstName,
        reporterId: reporter.id,
        email: reporter.email ?? undefined,
        title: reporter.title ?? undefined,
      });
      if (pressListId) {
        await addToList.mutateAsync({
          listId: pressListId,
          body: { contactId: result.data.id },
        });
      }
      navigate(`/contacts/${result.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add to Contacts</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Reporter preview */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center gap-3">
              {reporter.imageUrl ? (
                <img
                  src={reporter.imageUrl}
                  alt={reporter.name}
                  className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                  onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center flex-shrink-0 select-none">
                  {getInitials(reporter.name)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{reporter.name}</p>
                {reporter.title && (
                  <p className="text-xs text-gray-500 truncate">{reporter.title}</p>
                )}
                {primaryPubName && (
                  <p className="text-xs text-blue-600 truncate">{primaryPubName}</p>
                )}
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-200">
              {reporter.email ? (
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Email:</span> {reporter.email}
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">No email on file</p>
              )}
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <PressListSelector value={pressListId} onChange={setPressListId} />

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {submitting ? 'Adding...' : 'Add to Contacts'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── BatchImportModal ──────────────────────────────────────────────────────────

export interface BatchImportModalProps {
  reporters: Reporter[];
  onClose: () => void;
}

export function BatchImportModal({ reporters, onClose }: BatchImportModalProps) {
  const batchImport = useBatchImportReporters();
  const [pressListId, setPressListId] = useState('');
  const [result, setResult] = useState<BatchImportResult | null>(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await batchImport.mutateAsync({
        reporterIds: reporters.map((r) => r.id),
        pressListId: pressListId || undefined,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {result ? 'Import Complete' : `Add ${reporters.length} Reporter${reporters.length !== 1 ? 's' : ''} to Contacts`}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {result ? (
          /* Success state */
          <div className="px-6 py-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="space-y-1">
                {result.created.length > 0 && (
                  <p className="text-sm text-gray-900">
                    <span className="font-semibold">{result.created.length}</span> contact{result.created.length !== 1 ? 's' : ''} created
                  </p>
                )}
                {result.alreadyExists.length > 0 && (
                  <p className="text-sm text-gray-500">
                    <span className="font-medium">{result.alreadyExists.length}</span> already in contacts — skipped
                  </p>
                )}
                {result.created.length === 0 && result.alreadyExists.length === 0 && (
                  <p className="text-sm text-gray-500">No contacts were created.</p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Confirm state */
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Reporter list preview */}
            <div className="bg-gray-50 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2">
              {reporters.slice(0, 8).map((r) => (
                <div key={r.id} className="flex items-center gap-2">
                  {r.imageUrl ? (
                    <img
                      src={r.imageUrl}
                      alt={r.name}
                      className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                      onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-semibold text-xs flex items-center justify-center flex-shrink-0 select-none">
                      {getInitials(r.name)}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{r.name}</p>
                    {r.primaryPublication?.name && (
                      <p className="text-xs text-gray-500 truncate">{r.primaryPublication.name}</p>
                    )}
                  </div>
                </div>
              ))}
              {reporters.length > 8 && (
                <p className="text-xs text-gray-400 text-center pt-1">
                  +{reporters.length - 8} more
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <PressListSelector value={pressListId} onChange={setPressListId} />

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {submitting ? 'Adding...' : `Add ${reporters.length} to Contacts`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
